import { useState, useRef, useCallback } from "react";
import { useDesktopHeaderHeight } from "arena/components/AppShell/AppModeContext";

const ACTIVATION_DEBOUNCE_MS = 80;
const NAVIGATION_SCROLL_RETRY_MS = 60;

export default function useVideoNavigation({
  contents,
  loadingMore,
  handleLoadMore,
}) {
  const [activeIndex, setActiveIndex] = useState(0); // Индекс активного элемента
  const headerHeight = useDesktopHeaderHeight();

  // Refs для debounce логики
  const scrollTimeoutRef = useRef(null);

  const handleActivateDebounced = useCallback(
    (index) => {
      // Очищаем предыдущий таймаут
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Устанавливаем новый таймаут
      scrollTimeoutRef.current = setTimeout(() => {
        setActiveIndex(index);

        // Проверяем нужно ли загрузить больше контента при прокрутке
        if (index >= contents.length - 2 && !loadingMore) {
          handleLoadMore();
        }
      }, ACTIVATION_DEBOUNCE_MS);
    },
    [contents.length, loadingMore, handleLoadMore]
  );

  // 🔴 Исправление: навигация с поддержкой строк "next"/"previous"
  const handleNavigate = useCallback(
    (direction) => {
      let targetIndex;

      if (direction === "next") {
        targetIndex = activeIndex + 1;
      } else if (direction === "previous") {
        targetIndex = activeIndex - 1;
      } else if (typeof direction === "number") {
        targetIndex = direction;
      } else {
        return; // неизвестный тип направления
      }

      if (targetIndex >= 0 && targetIndex < contents.length) {
        // Прокручиваем к целевому элементу используя математический расчет
        if (typeof document !== "undefined") {
          const container = document.querySelector("[data-shorty-feed]");

          if (container) {
            // Рассчитываем высоту элемента математически
            // Это решает проблему с виртуализацией, когда элементы могут не существовать в DOM
            const isMobile = window.innerWidth < 900; // приблизительное определение мобильного устройства
            const elementHeight = isMobile
              ? window.innerHeight
              : window.innerHeight - headerHeight; // высота элемента

            const targetScrollTop = targetIndex * elementHeight;

            container.scrollTo({
              top: targetScrollTop,
              behavior: "smooth",
            });

            setTimeout(() => {
              container.scrollTo({
                top: targetScrollTop,
                behavior: "smooth",
              });
            }, NAVIGATION_SCROLL_RETRY_MS);
          }
        }

        // Проверяем нужно ли загрузить больше контента при навигации через кнопки
        if (targetIndex >= contents.length - 2 && !loadingMore) {
          handleLoadMore();
        }
      }
    },
    [activeIndex, contents.length, loadingMore, handleLoadMore, headerHeight]
  );

  // Cleanup при размонтировании
  const cleanup = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
  }, []);

  return {
    activeIndex,
    handleActivateDebounced,
    handleNavigate,
    cleanup,
  };
}
